import Conviva, { Analytics, Constants } from '../../lib/conviva-core-sdk.js';
import ConvivaVideoJs from '../../lib/conviva-videojs-module.js';
import videojs from 'video.js';
import { clampStringEllipsis } from '../../util';

// Set up ConvivaVideoJs module. Although it's wrapped in a CJS module, it only auto-initializes
// if it's included directly as a <script> tag in the page. Initialization, in this case, means
// attaching a couple of namespaces from ConvivaVideoJs to the main Conviva module after importing it.
Conviva.Impl.VideojsProxy = ConvivaVideoJs.Impl.VideojsProxy;
Conviva.ProxyMonitor = ConvivaVideoJs.ProxyMonitor;
const _convivaRef = new WeakMap();

// Define constants:
const DEFAULT_CUSTOMER_KEY_TEST = '2cb8327a07e33a8f08738209e3ce8d05c85d833f';
const MAX_TAG_LENGTH = 256;

const STREAM_PROTOCOL_MAPPING = Object.freeze({
    'video/mp4': 'MP4',
    'video/x-mp4-fragmented': 'Smooth Streaming',
    'audio/x-mpegurl': 'HLS',
    'video/x-mpeg-dash': 'DASH'
});

const PLAYER_TYPE_MAPPING = Object.freeze({
    None: 'None',
    NewStandardPlayer: 'Full Experience',
    PipPlayer: 'Pip',
    MiniPlayer: 'Mini',
    StandardPlayer: 'Standard',
    ScreenReader: 'Screen Reader',
    ContentOnlyPlayer: 'Content Only',
    SmartPlayer: 'Multiview',
    Composite: 'Composite' // Todo: determine actual name
});

const DEFAULT_CONTENT_INFO = Object.freeze({
    presentationId: '',
    playerType: 'Composite',
    duration: '0',
    recordedDate: '',
    site: '',
    referrer: location.href,
    streamProtocol: '',
    numberOfSlides: '0',
    numberOfVideos: '0',
    hasQuizzing: 'false',
    is360: 'false',
    playerVersion: '8.0',
    playerId: '',
    siteName: '',
    streamUrl: '',
    multisiteId: '',

    [Constants.STREAM_URL]: '',
    [Constants.ASSET_NAME]: '',
    [Constants.IS_LIVE]: false,
    [Constants.PLAYER_NAME]: 'player_8_0',
    [Constants.VIEWER_ID]: '',
    [Constants.DEFAULT_RESOURCE]: '',
    [Constants.DURATION]: 0,
    [Constants.ENCODED_FRAMERATE]: 0
});

const DEFAULT_METADATA_INFO = Object.freeze({
    brand: '',
    manufacturer: '',
    model: '',
    type: '',
    version: '',
    osName: '',
    osVersion: '',
    category: '',

    [Constants.DeviceMetadata.BRAND]: '',
    [Constants.DeviceMetadata.MANUFACTURER]: '',
    [Constants.DeviceMetadata.MODEL]: '',
    [Constants.DeviceMetadata.TYPE]: '',
    [Constants.DeviceMetadata.OS_NAME]: '',
    [Constants.DeviceMetadata.OS_VERSION]: '',
    [Constants.DeviceMetadata.CATEGORY]: '',
    [Constants.DeviceMetadata.VERSION]: ''
})

/**
 * The Conviva module.<br><br>
 * Note: this module is optional and might not be available on every MediasitePlayer instance.
 * @member {ConvivaPlugin|undefined} conviva
 * @memberof MediasitePlayer#
 */

/**
 * Configures Conviva analytics.<br><br>Note: this plugin currently has no configurable options and must be attached at player initialization time.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class ConvivaPlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     * @param {string} [options.presentationId]
     * @param {string} [options.viewerId]
     * @param {string} [options.title]
     * @param {number} [options.duration]
     */
    constructor(player, options) {
        super(player, options);
        this.log('Loaded Conviva plugin.');
    }

    /**
     * Initializes the Conviva plugin.
     * @param {ConvivaConfiguration} options
     */
    init(options) {
        const player = this.player;

        // Set up default settings
        const convivaSettings = options.serviceUrl ? { [Constants.GATEWAY_URL]: options.serviceUrl } : undefined;
        Analytics.init(options.customerId ?? DEFAULT_CUSTOMER_KEY_TEST, null, convivaSettings);

        const contentInfo = Object.assign({}, DEFAULT_CONTENT_INFO);

        // Merge available player info
        contentInfo.streamUrl = contentInfo[Constants.STREAM_URL] = player.src();
        contentInfo.streamProtocol = player.currentType();

        // Merge any options the user passed
        contentInfo.presentationId = options.presentationId ?? contentInfo.presentationId;
        contentInfo.playerType = PLAYER_TYPE_MAPPING[options.playerType ?? contentInfo.playerType]; // Todo: confirm the actual player type name we'll be using
        contentInfo.duration = `${options.duration ?? contentInfo.duration}`;
        contentInfo[Constants.DURATION] = contentInfo.duration;
        contentInfo.recordedDate = options.recordedDateString ?? contentInfo.recordedDate;
        contentInfo.site = window.location.host;
        contentInfo.referrer = document.referrer;
        contentInfo.streamProtocol = STREAM_PROTOCOL_MAPPING[options.streamType] ?? contentInfo.streamProtocol;
        contentInfo.numberOfSlides = `${options.numberOfSlides ?? contentInfo.numberOfSlides}`;
        contentInfo.numberOfVideos = `${options.numberOfVideos ?? contentInfo.numberOfVideos}`;
        contentInfo.hasQuizzing = options.hasQuizzing ?? contentInfo.hasQuizzing;
        contentInfo.is360 = options.is360 ?? contentInfo.is360;
        contentInfo.playerVersion = `${options.siteVersion}.${options.siteBuildNumber}`;
        contentInfo.playerId = `[${clampStringEllipsis(options.playerId, 9)}] ${options.playerName}`;
        contentInfo.siteName = options.siteName ?? contentInfo.siteName;
        contentInfo.streamUrl = clampStringEllipsis(options.streamUrl, MAX_TAG_LENGTH) ?? clampStringEllipsis(contentInfo.streamUrl, MAX_TAG_LENGTH);
        contentInfo.multisiteId = options.multisiteId ?? contentInfo.multisiteId;

        contentInfo[Constants.ASSET_NAME] = `[${clampStringEllipsis(options.presentationId, 9)}] ${options.title} [Video 1]`;
        contentInfo[Constants.PLAYER_NAME] = options.playerName;
        contentInfo[Constants.VIEWER_ID] = options.userNameHash ?? contentInfo[Constants.VIEWER_ID];
        contentInfo[Constants.STREAM_URL] = contentInfo.streamUrl;

        // Device Metadata
        const deviceMetadata = this.setupMetadata();

        this.log(`Initializing Conviva analytics with configuration: ${JSON.stringify(contentInfo)}`);

        // Init analytics
        const convivaVideoAnalytics = Analytics.buildVideoAnalytics();
        convivaVideoAnalytics.setPlayer(player);
        convivaVideoAnalytics.setContentInfo(contentInfo);
        Analytics.setDeviceMetadata(deviceMetadata);
        _convivaRef.set(this, convivaVideoAnalytics);

        player.on('play', () => void convivaVideoAnalytics.reportPlaybackRequested());
        player.on('ended', () => {
            void convivaVideoAnalytics.reportPlaybackEnded();

            //prep analystics to get ready to start another session if the user starts playback again after the video ends
            convivaVideoAnalytics.setPlayer(player);
            convivaVideoAnalytics.setContentInfo(contentInfo);
            Analytics.setDeviceMetadata(deviceMetadata);
        });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                Analytics.reportAppForegrounded();
            }
            else {
                Analytics.reportAppBackgrounded();
            }
        });

        window.addEventListener('beforeunload', () => {
            convivaVideoAnalytics.release();
            Analytics.release();
        });
    }

    setupMetadata() {
        const deviceMetadata = Object.assign({}, DEFAULT_METADATA_INFO);
        const foundMetadata = this.#getDeviceMetadata();

        deviceMetadata.browserName = foundMetadata?.browserName ?? deviceMetadata.browserName;
        deviceMetadata.osName = foundMetadata?.osName ?? deviceMetadata.osName;
        deviceMetadata[Constants.DeviceMetadata.OS_NAME] = deviceMetadata.osName;
        deviceMetadata.osVersion = foundMetadata?.osVersion ?? deviceMetadata.osVersion;
        deviceMetadata[Constants.DeviceMetadata.OS_VERSION] = deviceMetadata.osVersion;

        let manufactor = deviceMetadata.manufacturer;
        if (foundMetadata?.osName === "Macintosh") {
            manufactor = "Apple";
        }
        deviceMetadata.manufacturer = manufactor;
        deviceMetadata[Constants.DeviceMetadata.MANUFACTURER] = deviceMetadata.manufacturer;

        let deviceType = deviceMetadata.deviceType;
        let deviceCategory = deviceMetadata.category;
        if (foundMetadata?.browserName && foundMetadata.browserName !== "unknown") {
            if (foundMetadata.osName === "Windows" || foundMetadata.osName === "Macintosh") {
                deviceType = Constants.DeviceType.DESKTOP;
                deviceCategory = Constants.DeviceCategory.WEB;
            }            
        }
        deviceMetadata.version = deviceType;
        deviceMetadata.deviceCategory = deviceCategory;
        deviceMetadata[Constants.DeviceMetadata.TYPE] = deviceType;
        deviceMetadata[Constants.DeviceMetadata.CATEGORY] = deviceCategory;

        return deviceMetadata;
    }

    //pulled from https://medium.com/creative-technology-concepts-code/detect-device-browser-and-version-using-javascript-8b511906745
    #getDeviceMetadata() {
        if (!navigator || !window) {
            return {};
        }
        const module = {
            options: [],
            header: [navigator.platform, navigator.userAgent, navigator.appVersion, navigator.vendor, window.opera],
            dataos: [
                { name: 'Windows Phone', value: 'Windows Phone', version: 'OS' },
                { name: 'Windows', value: 'Win', version: 'NT' },
                { name: 'iPhone', value: 'iPhone', version: 'OS' },
                { name: 'iPad', value: 'iPad', version: 'OS' },
                { name: 'Kindle', value: 'Silk', version: 'Silk' },
                { name: 'Android', value: 'Android', version: 'Android' },
                { name: 'PlayBook', value: 'PlayBook', version: 'OS' },
                { name: 'BlackBerry', value: 'BlackBerry', version: '/' },
                { name: 'Macintosh', value: 'Mac', version: 'OS X' },
                { name: 'Linux', value: 'Linux', version: 'rv' },
                { name: 'Palm', value: 'Palm', version: 'PalmOS' }
            ],
            databrowser: [
                { name: 'Edge', value: 'Edg', version: 'Edg' },
                { name: 'Chrome', value: 'Chrome', version: 'Chrome' },
                { name: 'Firefox', value: 'Firefox', version: 'Firefox' },
                { name: 'Safari', value: 'Safari', version: 'Version' },
                { name: 'Internet Explorer', value: 'MSIE', version: 'MSIE' },
                { name: 'Opera', value: 'Opera', version: 'Opera' },
                { name: 'BlackBerry', value: 'CLDC', version: 'CLDC' },
                { name: 'Mozilla', value: 'Mozilla', version: 'Mozilla' }
            ],
            init: function () {
                var agent = this.header.join(' '),
                    os = this.matchItem(agent, this.dataos),
                    browser = this.matchItem(agent, this.databrowser);

                return { os: os, browser: browser };
            },
            matchItem: function (string, data) {
                var i = 0,
                    j = 0,
                    html = '',
                    regex,
                    regexv,
                    match,
                    matches,
                    version;

                for (i = 0; i < data.length; i += 1) {
                    regex = new RegExp(data[i].value, 'i');
                    match = regex.test(string);
                    if (match) {
                        regexv = new RegExp(data[i].version + '[- /:;]([\\d._]+)', 'i');
                        matches = string.match(regexv);
                        version = '';
                        if (matches) { if (matches[1]) { matches = matches[1]; } }
                        if (matches) {
                            matches = matches.split(/[._]+/);
                            for (j = 0; j < matches.length; j += 1) {
                                if (j === 0) {
                                    version += matches[j] + '.';
                                } else {
                                    version += matches[j];
                                }
                            }
                        } else {
                            version = '0';
                        }
                        return {
                            name: data[i].name,
                            version: parseFloat(version)
                        };
                    }
                }
                return { name: 'unknown', version: 0 };
            }
        };

        const detectModule = module.init();

        return {
            osName: detectModule.os.name,
            osVersion: detectModule.os.version,
            browserName: detectModule.browser.name,
            browserVersion: detectModule.browser.version,
            userAgent: navigator.userAgent,
            appVersion: navigator.appVersion,
            platform: navigator.platform,
            vendor: navigator.vendor
        }            
    }

    dispose() {
        _convivaRef.get(this)?.reportPlaybackEnded();
        super.dispose();
    }
}

export { ConvivaPlugin };

/**
 * @typedef {Object} ConvivaConfiguration
 * @property {string} presentationId
 * @property {string} serviceUrl
 * @property {string} customerId
 * @property {string} title
 * @property {boolean} isLive
 * @property {string} playerType
 * @property {number} duration
 * @property {string} recordedDateString
 * @property {number} numberOfSlides
 * @property {number} numberOfVideos
 * @property {string} playerId
 * @property {string} layoutId
 * @property {string} siteName
 * @property {string} siteVersion
 * @property {string} siteBuildNumber
 * @property {string} playerName
 * @property {string} userNameHash
 * @property {string} multisiteId
 * @property {string} streamType
 * @property {string} streamUrl
 */
